import { useState, useCallback, useRef } from 'react'

/**
 * Custom hook to manage voice selection and its effects on video data
 * Handles voice changes and triggers re-clustering when needed
 */
export const useVoiceManagement = ({
  formData,
  setFormData,
  onAudioClustering,
  formatShotListForClustering
} = {}) => {
  const [voices, setVoices] = useState([])
  const isFetchingShots = useRef(false) // Reference to check if shots are generating

  /**
   * Set the fetching shots reference
   */
  const setIsFetchingShots = useCallback(value => {
    isFetchingShots.current = value
  }, [])

  /**
   * Handle voice change with optional re-clustering
   */
  /**
   * Handle voice change with optional re-clustering
   * @param {string} voiceId - The voice ID to change to
   * @param {boolean} shouldClone - If true, signals that cloning should be handled by parent
   * @returns {object|undefined} - Returns { shouldClone: true, voiceId } if cloning is requested
   */
  /**
   * Handle voice change with optional re-clustering
   * @param {string} voiceId - The voice ID to change to
   * @param {boolean} shouldClone - If true, signals that cloning should be handled by parent
   * @param {string} characterKey - Optional key of the character to change voice for
   * @returns {object|undefined} - Returns { shouldClone: true, voiceId } if cloning is requested
   */
  const handleVoiceChange = useCallback(
    (voiceId, shouldClone = false, characterKey = null) => {
      // If shouldClone is true, return early - parent component will handle cloning
      if (shouldClone) {
        return { shouldClone: true, voiceId }
      }

      const currentShotList = formData?.shotList || []
      const hasGeneratedMedia = currentShotList.some(
        scene =>
          scene.audio_link ||
          scene.audio_duration ||
          scene.shots.some(shot => shot.vidSrc || shot.video_duration)
      )

      // Prepare updated shotList (clear duration-dependent assets if they exist)
      let updatedShotList = currentShotList
      if (hasGeneratedMedia) {
        updatedShotList = currentShotList.map(scene => ({
          ...scene,
          audio_link: null,
          audio_duration: null,
          audio_duration_estimated: true,
          shots: scene.shots.map(shot => ({
            ...shot,
            vidSrc: null,
            video_duration: null,
            audio_duration: null,
            generated_video_duration: null
          }))
        }))
      }

      // Update formData
      setFormData(prev => {
        const updatedData = {
          ...prev,
          voiceoverAudio: null,
          shotList: updatedShotList
        }

        if (characterKey) {
          updatedData.characterVoices = {
            ...(prev.characterVoices || {}),
            [characterKey]: voiceId
          }
        } else {
          updatedData.selectedVoice = voiceId
        }

        return updatedData
      })

      // Trigger Audio Clustering with new voice mapping
      // const hasShots = updatedShotList && updatedShotList.length > 0

      // if (hasShots && onAudioClustering && formatShotListForClustering) {
      //   const formattedData = formatShotListForClustering(updatedShotList)
      //   // Pass voiceId or updated characterVoices to ensure it uses new ones immediately
      //   const options = characterKey
      //     ? {
      //         character_voices: {
      //           ...(formData.characterVoices || {}),
      //           [characterKey]: voiceId
      //         }
      //       }
      //     : { voiceId }

      //   onAudioClustering(formattedData, null, options)
      // }
    },
    [
      formData?.shotList,
      formData?.characterVoices,
      setFormData,
      onAudioClustering,
      formatShotListForClustering
    ]
  )

  /**
   * Reset voice management state
   */
  const resetVoiceManagement = useCallback(() => {
    setVoices([])
    isFetchingShots.current = false
  }, [])

  return {
    // State
    voices,
    setVoices,

    // Actions
    handleVoiceChange,
    setIsFetchingShots,
    resetVoiceManagement
  }
}

export default useVoiceManagement
